import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getReport, getReportDetail } from 'services/report';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { IssueReport } from 'types/room';
import { ReportDetail } from 'types/report';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormDetailReportSchema } from 'utils/validate';
import { deleteReportIssue, updateAssetIssues } from 'services/locations';
import { useFlash } from 'contexts/flash';
import { appendDynamicOptionForPayload, appendDynamicSingleForPayload } from 'utils/convertData';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';

export type FormDetailReport = {
  description?: string;
  equipmentId: string;
  status?: string;
  actionBy?: string | null;
  actionByDate?: string | null;
  resolutionDate?: string;
  note?: string | null;
  name: string;
  actionByName?: string;
} & {
  [key: string]: unknown;
};
type DropdownValue = {
  value: string;
  name: string;
  id: number;
};
export type DropdownOption = {
  id: number;
  name: string;
  dropdownValues: DropdownValue[];
  options: { id: number; type: string; dropdownValueOptionId: number }[];
  required: boolean;
};
export type ListOptions = DropdownOption[];

export const useInjection = () => {
  const { issuesId } = useParams();
  const navigate = useNavigate();
  const { setFlash } = useFlash();
  const {
    handleSubmit,
    register,
    control,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<FormDetailReport>({
    resolver: yupResolver(FormDetailReportSchema)
  });
  const refInputFile = useRef<HTMLInputElement>(null);
  const isFirstLoad = useRef(true);

  const { systemDropdownValues, singleEntryField } = useFilteredRoomContext();

  const [data, setData] = useState<ReportDetail>();
  const [itemActive, setItemActive] = useState<IssueReport | null>(null);
  const [search, setSearch] = useState<string>('');
  const [photos, setPhotos] = useState<IssueReport[]>([]);
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [deletePhotos, setDeletePhotos] = useState<number[]>([]);
  const paths = useMemo(() => {
    return ['Locations', data?.room?.name, data?.equipmentId];
  }, [data]);
  const [options, setOptions] = useState<ListOptions>();
  const [reportList, setReportList] = useState<ReportDetail[]>([]);

  const handleSelectReport = useCallback(
    (id: number) => {
      const newPath = window.location.pathname.replace(`/issues/${issuesId}/`, `/issues/${id}/`);
      navigate(newPath);
    },
    [issuesId, navigate]
  );

  const fetchReportList = useCallback(
    async (assetId: number) => {
      try {
        const res = await getReport({ submittedAssetId: assetId, size: 100, page: 1 });
        if (res?.reports) {
          setReportList(res.reports);
          if (isFirstLoad.current && res.reports.length > 0) {
            isFirstLoad.current = false;
            const latestId = res.reports[0].id;
            if (latestId !== Number(issuesId)) {
              handleSelectReport(latestId);
            }
          }
        }
      } catch (error) {
        console.error('Fetch report list failed', error);
      }
    },
    [issuesId, handleSelectReport]
  );

  const handelGetDetail = useCallback(async () => {
    if (!issuesId) return;
    const data = (await getReportDetail(Number(issuesId))) as { result: ReportDetail };
    setData(data.result);
    setPhotos(data.result?.issueReportPhotos);
    setNewPhotos([]);
    setDeletePhotos([]);
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const reportIssueOptions: Record<string, any> = {};

    systemDropdownValues?.forEach((dropdownValue) => {
      reportIssueOptions[`option_${dropdownValue.id}`] = '';
      reportIssueOptions[`option_${dropdownValue.id}_name`] = '';
    });
    singleEntryField?.forEach((field) => {
      reportIssueOptions[`single_${field.id}`] = '';
    });

    data.result.options?.forEach((issueOption) => {
      if (issueOption.fieldType === 'DROPDOWN') {
        systemDropdownValues?.forEach((dropdownValue) => {
          const hasRoomType = dropdownValue.id.toString() === issueOption.type;

          if (hasRoomType) {
            const index = dropdownValue.dropdownValues.some(
              (dropdown) => dropdown.value === issueOption.value
            );

            reportIssueOptions[`option_${issueOption?.type}`] = index
              ? issueOption?.value
              : 'OTHER';
            reportIssueOptions[`option_${issueOption?.type}_name`] = issueOption?.value;
          }
        });
      } else {
        reportIssueOptions[`single_${issueOption?.type}`] = issueOption?.value;
      }
    });

    reset({
      description: data.result.description,
      equipmentId: data.result.equipmentId,
      complianceIssue: data.result.complianceIssue,
      actionNeeded: data.result.actionNeeded,
      type: data.result.type,
      actionBy: !data.result.actionBy?.startsWith('other_') ? data.result.actionBy : 'OTHER',
      actionByName: !data.result.actionBy?.startsWith('other_')
        ? ''
        : data.result.actionBy?.replace('other_', ''),
      actionByDate: data.result.actionByDate,
      resolutionDate: data.result.resolutionDate,
      status: data.result.status,
      note: data.result.note,
      name: data.result.name,
      ...reportIssueOptions
    });
    if (data.result?.submittedAsset?.id) {
      fetchReportList(data.result.submittedAsset.id);
    }
  }, [issuesId, reset, fetchReportList, singleEntryField, systemDropdownValues]);

  const { asyncCallback } = useAsyncCallback(handelGetDetail, [issuesId]);

  useEffect(() => {
    asyncCallback();
  }, [asyncCallback]);

  useEffect(() => {
    setOptions(systemDropdownValues as any);
  }, [systemDropdownValues]);

  const handleExpand = useCallback((dateSelect?: IssueReport) => {
    if (dateSelect) {
      setItemActive(dateSelect);
      return;
    }
    setItemActive(null);
  }, []);

  const handleSubmitForm = useCallback(
    async (valueForm: FormDetailReport) => {
      await updateAssetIssues({
        issuesId: Number(data?.id),
        assetId: Number(data?.submittedAsset.id),
        submitId: Number(data?.submittedAsset?.submitId),
        payload: {
          name: valueForm.name,
          actionBy:
            valueForm.actionBy === 'OTHER'
              ? `other_${valueForm.actionByName}`
              : valueForm.actionBy || '',
          actionByDate: valueForm.actionByDate || '',
          resolutionDate: valueForm.status === 'ACTIVE' ? null : valueForm.resolutionDate,
          equipmentId: valueForm.equipmentId,
          description: valueForm.description,
          status: valueForm.status,
          note: valueForm.note,
          options: JSON.stringify(appendDynamicOptionForPayload(valueForm)),
          singleFields: JSON.stringify(appendDynamicSingleForPayload(valueForm)),
          deletePhotoIds: deletePhotos.length > 0 ? JSON.stringify(deletePhotos) : undefined,
          files: newPhotos
        }
      });
      setValue(
        'resolutionDate',
        valueForm.status === 'RESOLVED' && !valueForm.resolutionDate ? new Date().toISOString() : ''
      );
      setNewPhotos([]);
      setDeletePhotos([]);
      asyncCallback();
      setFlash({ message: 'Update Report successful!', type: 'success' });
    },
    [
      data?.id,
      data?.submittedAsset.id,
      data?.submittedAsset?.submitId,
      deletePhotos,
      newPhotos,
      setValue,
      setFlash,
      asyncCallback
    ]
  );

  const { asyncCallback: asyncHandleSubmitForm } = useAsyncCallback(handleSubmitForm, [
    handleSubmitForm
  ]);

  return {
    search,
    setSearch,
    paths,
    handleExpand,
    itemActive,
    handleSubmit: handleSubmit(asyncHandleSubmitForm),
    register,
    reset,
    errors,
    control,
    handleSelectReport,
    setValue,
    options,
    reportList,
    issuesId,
    photos,
    refInputFile,
    newPhotos,
    watch,
    onDeleteIssue: useCallback(async () => {
      if (window.confirm('Are you sure you want to delete this issue?')) {
        try {
          await deleteReportIssue({
            issueId: Number(data?.id),
            id: Number(data?.submittedAsset.id),
            submitted: Number(data?.submittedAsset?.submitId)
          });
          setFlash({ type: 'success', message: 'Issue deleted successfully!' });
          window.history.go(-1);
        } catch (error) {
          setFlash({ type: 'error', message: 'Delete the issue failed' });
        }
      }
    }, [setFlash, data]),
    onUploadFile: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const renewPhotos = [...newPhotos];
        if (e.target.files?.[0]) {
          renewPhotos.push(e.target.files[0]);
          setNewPhotos(renewPhotos);
        }
      },
      [newPhotos]
    ),
    onDeleteNewPhoto: (index: number) => {
      const renewPhotos = [...newPhotos];
      renewPhotos.splice(index, 1);
      setNewPhotos(renewPhotos);
    },
    onDeleteActivePhoto: (index: number) => {
      if (window.confirm('Are you sure you want to delete this photo?')) {
        deletePhotos.push(photos[index].id);
        const renewPhotos = [...photos];
        renewPhotos.splice(index, 1);
        setPhotos(renewPhotos);
        setDeletePhotos(deletePhotos);
      }
    }
  };
};
